import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { getWidgetConfig } from 'src/app/widget-config-store';

/**
 * Fuente de verdad del backend activo.
 * En web component: el host pide api-url → setBaseUrl() → todos los getters
 * derivan de esa URL. En SPA: usa environment.baseUrl (compilado).
 */
@Injectable({ providedIn: 'root' })
export class ApiUrlService {
  private _baseUrl: string = environment.baseUrl;

  get baseUrl(): string {
    return this._baseUrl;
  }

  //dev|qa|pro — afecta scraperMock, OTP length, debug toggle
  get runtimeEnv(): string {
    const config = getWidgetConfig();
    if (config.runtimeEnv) {
      return config.runtimeEnv;
    }
    try {
      const hostname = new URL(this._baseUrl).hostname;
      const derived = hostname.includes('.qa.') ? 'qa'
        : hostname.includes('.dev.') ? 'dev'
        : 'pro';
      return derived;
    } catch {
      return environment.env;
    }
  }

  /**
   * true cuando el backend activo es dev o qa. Úsalo para logs de debug en vez
   * de environment.production: el script de deploy genera environment.ts con
   * production=true en todos los tiers, así que ese flag no distingue entornos.
   */
  get isNonProdRuntime(): boolean {
    const env = this.runtimeEnv;
    return env === 'dev' || env === 'qa';
  }

  // WebSocket de notificaciones: api.xxx → wss://notifications.xxx
  get notificationsUrl(): string {
    const config = getWidgetConfig();
    if (config.notificationsUrl) {
      try {
        const parsed = new URL(config.notificationsUrl.trim());
        if (parsed.protocol === 'ws:' || parsed.protocol === 'wss:') {
          return config.notificationsUrl;
        }
      } catch { /* invalid URL, fall through */ }
    }
    try {
      const parsed = new URL(this._baseUrl);
      if (!parsed.hostname.startsWith('api.')) {
        const fallback = environment.notificationsBaseUrl;
        return fallback;
      }
      const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = 'notifications.' + parsed.hostname.slice('api.'.length);
      const derived = `${wsProtocol}//${host}`;
      return derived;
    } catch {
      const fallback = environment.notificationsBaseUrl;
      return fallback;
    }
  }

  //belender (normal) u opendata (bcds) — afecta scraperMock y OTP length
  get organization(): 'belender' | 'opendata' {
    try {
      const hostname = new URL(this._baseUrl).hostname;
      const derived = hostname.includes('opendata') ? 'opendata' : 'belender';
      return derived;
    } catch {
      return 'belender';
    }
  }

  // true → OTP va a /mockscrapper; false → endpoint real
  get scraperMock(): boolean {
    return this.organization === 'opendata' && this.runtimeEnv === 'dev'
      || this.runtimeEnv === 'qa';
  }

  // Llamado por el host al pasar api-url. Valida formato, normaliza y guarda.
  setBaseUrl(url: string): void {
    const trimmed = (url ?? '').trim();
    if (!trimmed) return;
    try {
      const parsed = new URL(trimmed);
      const isHttps = parsed.protocol === 'https:';
      const isHttp = parsed.protocol === 'http:';
      if (!isHttps && !isHttp) {
        console.warn(`[ApiUrlService] Invalid protocol: ${parsed.protocol}`);
        return;
      }
      if (environment.production && isHttp &&
          parsed.hostname !== 'localhost' &&
          parsed.hostname !== '127.0.0.1' &&
          parsed.hostname !== '::1') {
        console.warn('[ApiUrlService] Refusing insecure http baseUrl in production.');
        return;
      }
      parsed.search = '';
      parsed.hash = '';
      this._baseUrl = (parsed.origin + parsed.pathname).replace(/\/$/, '');
    } catch {
      console.warn(`[ApiUrlService] Invalid URL: ${url}`);
    }
  }
}
